// @refresh reset
import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useClientReady } from '@/lib/initialRender';
import { type DeliveryDetails, type DeliveryCostResult } from "@/lib/shopifyApi";
import { useConsent } from './ConsentContext';
import { analyticsItems } from '@/lib/googleAnalytics';
import { browserGoogleMeasurement } from '@/lib/googleMeasurementBrowser';
import { priceAmountGrosze, withCartAmount } from '@/lib/cartMoney';
import { isCartAddon, isValidSavedDelivery, restoreCartItems } from '@/lib/cartItems';
export { isCartAddon } from '@/lib/cartItems';
export { ORIGIN_LABELS, deliveryGroupKey } from '@/lib/cartDelivery';

const STORAGE_KEY_ITEMS = "bukietowa_cart_items";
const STORAGE_KEY_DELIVERY = "bukietowa_cart_delivery";

function loadFromStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage quota or private-mode errors — silently ignore
  }
}

function removeFromStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export interface CartItemAttribute {
  key: string;
  value: string;
}

export interface SavedDelivery {
  details: DeliveryDetails;
  cost: DeliveryCostResult;
}

export interface CartItem {
  /** Unique per cart line — used for reliable removal */
  id: string;
  variantId: string;
  variantTitle: string;
  productTitle: string;
  price: string;
  /** Canonical unit price in grosze. Older saved carts are migrated on read. */
  unitAmountGrosze?: number;
  image: string | null;
  quantity: number;
  attributes?: CartItemAttribute[];
  /** Add-ons are paid Shopify lines, but they cannot exist without this parent cart line. */
  parentItemId?: string;
  /** Stable catalog identity used to toggle an add-on in the post-add panel. */
  addonId?: string;
  /** Attribute-priced add-ons are displayed and totalled locally, but are not Shopify variants. */
  isTextOnlyAddon?: boolean;
  /** Delivery snapshot captured at add-to-cart time. Each line carries its own. */
  delivery?: SavedDelivery;
}

export interface CartAddonInput {
  addonId: string;
  variantId: string | null;
  productTitle: string;
  price: string;
  image: string | null;
}

function loadCartItems(): CartItem[] {
  return restoreCartItems(loadFromStorage<unknown>(STORAGE_KEY_ITEMS));
}

function loadSavedDelivery(): SavedDelivery | null {
  const raw = loadFromStorage<SavedDelivery>(STORAGE_KEY_DELIVERY);
  return raw && isValidSavedDelivery(raw) ? raw : null;
}

/* ── Shared utilities (used by CartContext & checkout page) ── */
export function parsePricePLN(price: string): number {
  return (priceAmountGrosze(price) ?? 0) / 100;
}

/* ── Context interface ───────────────────────────────────── */
interface CartContextValue {
  items: CartItem[];
  totalCount: number;
  addItem: (item: Omit<CartItem, "quantity" | "id">) => void;
  /**
   * Add a group of cart items atomically — bouquet first, then Shopify-backed addons.
   * Preview items are shown only in the confirmation modal and never persisted in the cart.
   */
  addBatch: (
    items: Omit<CartItem, "quantity" | "id">[],
    previewItems?: Omit<CartItem, "quantity" | "id">[],
  ) => void;
  /** Remove a specific cart line by its unique id */
  removeItem: (id: string) => void;
  /** Adjust quantity of a cart line by delta; removes the line when qty would drop below 1 */
  updateQuantity: (id: string, delta: number) => void;
  updateOrderDetails: (id: string, patch: { delivery?: SavedDelivery; attributes?: CartItemAttribute[] }) => void;
  /** Add or remove one add-on from a specific bouquet bundle. */
  toggleAddon: (parentItemId: string, addon: CartAddonInput) => void;
  /** Navigate directly to /checkout */
  proceedToCheckout: () => void;
  /** Last delivery set on any product page — used as fallback & for UI hints */
  savedDelivery: SavedDelivery | null;
  setSavedDelivery: (d: SavedDelivery | null) => void;
  /** Clear the cart after a successful payment */
  clearCart: () => void;
  /** All items added in the last add-to-cart action — drives the confirmation modal */
  lastAddedBatch: CartItem[];
  clearLastAdded: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { preferences } = useConsent();
  const measureAdded = useCallback((added: CartItem[]) => {
    if (!preferences?.analytics) return;
    const tracker = browserGoogleMeasurement();
    void tracker.updateConsent({ analytics: true, marketing: preferences.marketing }).then(() => {
      tracker.analytics.ecommerce('add_to_cart', analyticsItems(added.map(item => ({ ...item, variantId: item.isTextOnlyAddon ? 'attribute-addon' : item.variantId }))));
    }).catch(() => { /* Optional measurement must not affect the cart. */ });
  }, [preferences?.analytics, preferences?.marketing]);
  const clientReady = useClientReady();
  const [, navigate] = useLocation();
  const [items, setItems] = useState<CartItem[]>(loadCartItems);
  const [savedDelivery, setSavedDelivery] = useState<SavedDelivery | null>(loadSavedDelivery);
  const [lastAddedParentId, setLastAddedParentId] = useState<string | null>(null);

  const updateOrderDetails = useCallback((id: string, patch: { delivery?: SavedDelivery; attributes?: CartItemAttribute[] }) => {
    setItems(previous => previous.map(item => {
      if (item.id !== id && item.parentItemId !== id) return item;
      const attributes = patch.attributes
        ? [...(item.attributes ?? []).filter(attribute => !patch.attributes!.some(next => next.key === attribute.key)), ...patch.attributes]
        : item.attributes;
      const delivery = patch.delivery ?? item.delivery;
      const recipientName = attributes?.find(attribute => attribute.key === 'Imię odbiorcy')?.value;
      const recipientPhone = attributes?.find(attribute => attribute.key === 'Telefon odbiorcy')?.value;
      return { ...item, attributes, delivery: delivery ? { ...delivery, details: {
        ...delivery.details,
        ...(recipientName !== undefined ? { recipientName } : {}),
        ...(recipientPhone !== undefined ? { recipientPhone } : {}),
      } } : undefined };
    }));
  }, []);

  // Persist cart items to localStorage on every change
  useEffect(() => {
    if (items.length > 0) {
      saveToStorage(STORAGE_KEY_ITEMS, items);
    } else {
      removeFromStorage(STORAGE_KEY_ITEMS);
    }
  }, [items]);

  // Persist savedDelivery to localStorage on every change
  useEffect(() => {
    if (savedDelivery) {
      saveToStorage(STORAGE_KEY_DELIVERY, savedDelivery);
    } else {
      removeFromStorage(STORAGE_KEY_DELIVERY);
    }
  }, [savedDelivery]);

  const totalCount = items.reduce((sum, i) => sum + i.quantity, 0);

  const lastAddedBatch = useMemo(() => {
    if (!lastAddedParentId) return [];
    return items.filter(item => item.id === lastAddedParentId || item.parentItemId === lastAddedParentId);
  }, [items, lastAddedParentId]);

  const clearLastAdded = useCallback(() => setLastAddedParentId(null), []);

  /** Insert a single CartItem into the items list (shared logic). */
  function insertItem(prev: CartItem[], newItem: CartItem): CartItem[] {
    if (isCartAddon(newItem) && (!newItem.parentItemId || !prev.some(item => item.id === newItem.parentItemId))) {
      return prev;
    }
    if (newItem.attributes?.length || newItem.delivery) return [...prev, newItem];
    const existing = prev.find(
      (i) => i.variantId === newItem.variantId && !i.attributes?.length && !i.delivery,
    );
    if (existing) return prev.map((i) => (i.id === existing.id ? { ...i, quantity: i.quantity + 1 } : i));
    return [...prev, newItem];
  }

  const addItem = useCallback((item: Omit<CartItem, "quantity" | "id">) => {
    if (isCartAddon(item)) return;
    const newItem: CartItem = withCartAmount({ ...item, id: crypto.randomUUID(), quantity: 1 });
    setLastAddedParentId(newItem.id);
    setItems((prev) => insertItem(prev, newItem));
    measureAdded([newItem]);
  }, [measureAdded]);

  /**
   * Add multiple cart items as one batch. Text-only addons are persisted as bundle
   * children and mirrored to the bouquet attribute used for server-side pricing.
   */
  const addBatch = useCallback((
    rawItems: Omit<CartItem, "quantity" | "id">[],
    previewItems: Omit<CartItem, "quantity" | "id">[] = [],
  ) => {
    if (rawItems.length === 0) return;
    const parentItemId = crypto.randomUUID();
    const newItems = rawItems.map((item, index) => withCartAmount({
      ...item,
      id: index === 0 ? parentItemId : crypto.randomUUID(),
      quantity: 1,
      parentItemId: index === 0 ? undefined : parentItemId,
    } as CartItem));
    const preview = previewItems.map((item) => withCartAmount({
      ...item,
      id: crypto.randomUUID(),
      quantity: 1,
      parentItemId,
      addonId: item.addonId ?? item.variantId.replace(/^addon-preview:/, ''),
      isTextOnlyAddon: true,
    } as CartItem));
    setLastAddedParentId(parentItemId);
    setItems((prev) => {
      const next = [...newItems, ...preview].reduce((acc, item) => insertItem(acc, item), prev);
      return preview.length > 0 ? syncTextAddonAttribute(next, parentItemId) : next;
    });
    measureAdded([...newItems, ...preview]);
  }, [measureAdded]);

  function syncTextAddonAttribute(allItems: CartItem[], parentItemId: string): CartItem[] {
    const names = allItems
      .filter(item => item.parentItemId === parentItemId && item.isTextOnlyAddon)
      .map(item => item.productTitle);
    return allItems.map(item => {
      if (item.id !== parentItemId) return item;
      const attributes = (item.attributes ?? []).filter(attribute => attribute.key !== 'Dodatki');
      if (names.length > 0) attributes.push({ key: 'Dodatki', value: names.join(', ') });
      return { ...item, attributes: attributes.length > 0 ? attributes : undefined };
    });
  }

  const toggleAddon = useCallback((parentItemId: string, addon: CartAddonInput) => {
    setItems(prev => {
      const parent = prev.find(item => item.id === parentItemId && !isCartAddon(item));
      if (!parent) return prev;
      const existing = prev.find(item =>
        item.parentItemId === parentItemId
        && (item.addonId === addon.addonId || item.variantId === addon.variantId),
      );
      if (existing) {
        const removed = removeItemTree(prev, existing.id);
        return existing.isTextOnlyAddon ? syncTextAddonAttribute(removed, parentItemId) : removed;
      }

      const isTextOnlyAddon = !addon.variantId;
      const newAddon: CartItem = withCartAmount({
        id: crypto.randomUUID(),
        addonId: addon.addonId,
        variantId: addon.variantId ?? `addon-preview:${addon.addonId}`,
        variantTitle: addon.productTitle,
        productTitle: addon.productTitle,
        price: addon.price,
        image: addon.image,
        quantity: 1,
        parentItemId,
        isTextOnlyAddon,
        attributes: [{ key: 'Do zamówienia', value: parent.productTitle }],
        delivery: parent.delivery,
      });
      const next = [...prev, newAddon];
      return isTextOnlyAddon ? syncTextAddonAttribute(next, parentItemId) : next;
    });
  }, []);

  function removeItemTree(prev: CartItem[], id: string): CartItem[] {
    const idsToRemove = new Set([id]);
    let foundChild = true;
    while (foundChild) {
      foundChild = false;
      for (const item of prev) {
        if (item.parentItemId && idsToRemove.has(item.parentItemId) && !idsToRemove.has(item.id)) {
          idsToRemove.add(item.id);
          foundChild = true;
        }
      }
    }
    return prev.filter(item => !idsToRemove.has(item.id));
  }

  /** Remove one add-on, or a main cart line together with all of its add-ons. */
  const removeItem = useCallback((id: string) => {
    setItems((prev) => {
      const removedItem = prev.find(item => item.id === id);
      const next = removeItemTree(prev, id);
      if (next.length === 0) setSavedDelivery(null);
      return removedItem?.isTextOnlyAddon && removedItem.parentItemId
        ? syncTextAddonAttribute(next, removedItem.parentItemId)
        : next;
    });
  }, []);

  /** Adjust quantity by delta; removes the line if new qty < 1 */
  const updateQuantity = useCallback((id: string, delta: number) => {
    setItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (!item) return prev;
      if (item.isTextOnlyAddon && delta > 0) return prev;
      const newQty = item.quantity + delta;
      if (newQty < 1) {
        const next = removeItemTree(prev, id);
        if (next.length === 0) setSavedDelivery(null);
        return item.isTextOnlyAddon && item.parentItemId
          ? syncTextAddonAttribute(next, item.parentItemId)
          : next;
      }
      return prev.map((i) => (i.id === id ? { ...i, quantity: newQty } : i));
    });
  }, []);

  /** Clear entire cart after successful payment */
  const clearCart = useCallback(() => {
    setItems([]);
    setLastAddedParentId(null);
    setSavedDelivery(null);
    removeFromStorage(STORAGE_KEY_ITEMS);
    removeFromStorage(STORAGE_KEY_DELIVERY);
  }, []);

  /** Navigate to /checkout — delivery is configured on the checkout page */
  const proceedToCheckout = useCallback(() => {
    if (items.length === 0) return;
    navigate('/checkout');
  }, [items, navigate]);

  return (
    <CartContext.Provider
      value={{
        items: clientReady ? items : [],
        totalCount: clientReady ? totalCount : 0,
        addItem,
        addBatch,
        removeItem,
        updateQuantity,
        updateOrderDetails,
        toggleAddon,
        proceedToCheckout,
        savedDelivery: clientReady ? savedDelivery : null,
        setSavedDelivery,
        clearCart,
        lastAddedBatch,
        clearLastAdded,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
